import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import Layout from "@/components/Layout";

import Home from "@/pages/Home";
import Community from "@/pages/Community";
import Category from "@/pages/Category";
import Thread from "@/pages/Thread";
import NewDiscussion from "@/pages/NewDiscussion";
import Guides from "@/pages/Guides";
import Guide from "@/pages/Guide";
import Glossary from "@/pages/Glossary";
import Challenges from "@/pages/Challenges";
import Challenge from "@/pages/Challenge";
import Newsletter from "@/pages/Newsletter";
import ConfirmNewsletter from "@/pages/ConfirmNewsletter";
import SuccessStories from "@/pages/SuccessStories";
import AdminModeration from "@/pages/AdminModeration";
import AdminSEO from "@/pages/AdminSEO";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminDiscussions from "@/pages/AdminDiscussions";
import AdminReplies from "@/pages/AdminReplies";
import AdminMembers from "@/pages/AdminMembers";
import AdminCategories from "@/pages/AdminCategories";
import SignIn from "@/pages/SignIn";
import Join from "@/pages/Join";
import Onboarding from "@/pages/Onboarding";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";

const wrap = (el) => <Layout>{el}</Layout>;

function NotFound() {
  return (
    <div className="mx-auto max-w-lg px-6 py-24 text-center">
      <h1 className="font-display text-5xl text-plum">Page not found</h1>
      <p className="mt-3 text-mutedplum text-lg">This path wandered off. Let's get you back to the Circle.</p>
      <Link to="/" className="mt-6 inline-block rounded-full bg-teal px-7 py-3 font-semibold text-white hover:bg-teal-dark transition-colors">Back home</Link>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Emergent Google auth returns to {origin}/onboarding#session_id=...
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={wrap(<Home />)} />
      <Route path="/community" element={wrap(<Community />)} />
      <Route path="/community/:categorySlug" element={wrap(<Category />)} />
      <Route path="/community/:categorySlug/:threadSlug" element={wrap(<Thread />)} />
      <Route path="/new-discussion" element={wrap(<NewDiscussion />)} />
      <Route path="/guides" element={wrap(<Guides />)} />
      <Route path="/guides/:slug" element={wrap(<Guide />)} />
      <Route path="/glossary" element={wrap(<Glossary />)} />
      <Route path="/challenges" element={wrap(<Challenges />)} />
      <Route path="/challenges/:slug" element={wrap(<Challenge />)} />
      <Route path="/newsletter" element={wrap(<Newsletter />)} />
      <Route path="/confirm-newsletter" element={wrap(<ConfirmNewsletter />)} />
      <Route path="/success-stories" element={wrap(<SuccessStories />)} />
      <Route path="/admin" element={wrap(<AdminDashboard />)} />
      <Route path="/admin/moderation" element={wrap(<AdminModeration />)} />
      <Route path="/admin/discussions" element={wrap(<AdminDiscussions />)} />
      <Route path="/admin/replies" element={wrap(<AdminReplies />)} />
      <Route path="/admin/members" element={wrap(<AdminMembers />)} />
      <Route path="/admin/categories" element={wrap(<AdminCategories />)} />
      <Route path="/admin/seo" element={wrap(<AdminSEO />)} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/join" element={<Join />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={wrap(<NotFound />)} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
